import {
  Button,
  Cog6ToothIcon,
  Dialog,
  Flex,
  HelpHover,
  LinkIcon,
  Menu,
  Panel,
  QrCodeIcon,
  Text,
  UserAvatar,
} from 'components'
import { useEffect, useState } from 'react'
import { useDisclosure, useUser } from 'utils'
import { useEventInfo } from '../../../pages/EventInfoPage'
import { useEvent } from '../../../utils/eventUtils'
import EventMembers from '../EventMembers'
import EventRegModeTile from '../EventRegModeTile'
import PublicQrCode from '../MomentsButton/PublicQrCode'

export default function EventBannerConfigBar() {
  const { event } = useEvent()
  const eventManager = useUser(event?.managerId)

  if (!event) return null

  const { managerId, memberIds, registrationMode, url } = event
  const cleanEventUrl = url?.match(/(?:https?:\/\/)?(?:www\.)?([^\/]+)/)?.[1]

  return (
    <Flex columnGap={10} rowGap={4} flexWrap="wrap">
      <Flex align="center" gap={2}>
        <UserAvatar user={eventManager} />
        <Flex direction="column">
          <Text textStyle="bodySmall">{eventManager?.displayName}</Text>
          <Text textStyle="bodyXSmall" opacity={0.6}>
            Event Manager
          </Text>
        </Flex>
      </Flex>

      <EventMembers ids={memberIds} managerId={managerId} />

      {registrationMode !== 'none' && <EventRegModeTile />}

      {cleanEventUrl && (
        <Flex align="center" gap={2} onClick={() => window.open(url, '_blank')} cursor="pointer">
          <Panel p={2} borderRadius="xl">
            <LinkIcon size="sm" />
          </Panel>
          <Flex direction="column">
            <Text textStyle="bodySmall">{cleanEventUrl}</Text>
            <Text textStyle="bodyXSmall" opacity={0.6}>
              Event Link
            </Text>
          </Flex>
        </Flex>
      )}

      <PublicQRInfo />
    </Flex>
  )
}

function PublicQRInfo() {
  const { updateMoments } = useEventInfo()
  const { event } = useEvent()
  const { open: openPublicQR, onToggle: togglePublicQR } = useDisclosure()
  const [selectedMomentId, setSelectedMomentId] = useState<number>()

  const publicMoments = event?.moments?.filter(m => m.type === 'public') || []
  const selectedMoment = publicMoments.find(m => m.id === selectedMomentId)

  useEffect(() => {
    setSelectedMomentId(publicMoments[0]?.id)
  }, [event])

  function handleManage() {
    togglePublicQR()
    updateMoments(selectedMomentId ?? false)
  }

  if (!publicMoments.length) return null

  return (
    <>
      <Flex align="center" gap={2} onClick={togglePublicQR} cursor="pointer">
        <Panel p={2} borderRadius="full">
          <QrCodeIcon size="sm" />
        </Panel>
        <Flex direction="column">
          <Flex alignItems="center" gap={1}>
            <Text textStyle="bodySmall">Attendee Access</Text>
            <HelpHover
              iconProps={{ size: 'sm' }}
              text="Public QR Code used for attendee self registration"
            />
          </Flex>
          <Text textStyle="bodyXSmall" color="primary.500" opacity={0.6}>
            View Code
          </Text>
        </Flex>
      </Flex>

      <Dialog
        open={openPublicQR}
        onClose={togglePublicQR}
        size={{ base: 'sm', sm: 'lg' }}
        title={
          <Flex alignItems="center" gap={2}>
            <QrCodeIcon />
            <Text textStyle="h5">Event QR Code</Text>
          </Flex>
        }
      >
        <Flex alignItems="stretch" direction="column" gap={8} p={{ base: 3, sm: 6 }}>
          {publicMoments.length > 1 && (
            <Menu
              alignSelf="flex-start"
              label="Select Moment"
              onChange={setSelectedMomentId}
              options={publicMoments.map(m => ({ value: m.id, label: m.title }))}
              value={selectedMomentId}
              variant="outline"
            />
          )}
          <Text alignSelf="center" textStyle="h5">
            {selectedMoment?.title}
          </Text>
          {selectedMomentId && (
            <PublicQrCode
              extraAction={
                <Button
                  alignSelf="center"
                  leftIcon={<Cog6ToothIcon />}
                  onClick={handleManage}
                  variant="outline"
                >
                  Manage
                </Button>
              }
              size={250}
              momentId={selectedMomentId}
            />
          )}
        </Flex>
      </Dialog>
    </>
  )
}
